import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageHeader from '../../components/PageHeader';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const ServicesOverview = () => {
  const steps = [
    { num: '01', title: 'Listen', desc: 'We start by understanding your culture, operations, and specific challenges. No canned assumptions.', icon: FiIcons.FiHeadphones },
    { num: '02', title: 'Assess', desc: 'Evaluate risks, requirements, and compliance gaps against actual capabilities.', icon: FiIcons.FiSearch },
    { num: '03', title: 'Build', desc: 'Draft or revise practical plans, procedures, and programs that fit your reality.', icon: FiIcons.FiTool },
    { num: '04', title: 'Train', desc: 'Educate your people so they possess the confidence and skills to execute.', icon: FiIcons.FiUsers },
    { num: '05', title: 'Test', desc: 'Validate everything through realistic drills and exercises.', icon: FiIcons.FiActivity },
    { num: '06', title: 'Improve', desc: 'Measure findings, track corrective actions, and continuously refine the system.', icon: FiIcons.FiTrendingUp }
  ];

  const services = [
    { title: 'Emergency Management & Planning', path: '/services/emergency-planning', desc: 'EAPs, ERPs, BCPs, and EOC procedures customized to your operations.' },
    { title: 'Compliance & Safety Consulting', path: '/services/compliance-consulting', desc: 'Gap assessments and reviews for OSHA, EPA, PHMSA, and NFPA alignment.' },
    { title: 'Drills & Exercises', path: '/services/drills-exercises', desc: 'HSEEP-aligned drills, tabletops, and full-scale exercises.' },
    { title: 'Training & Development', path: '/services/training-development', desc: 'Custom course development and train-the-trainer programs.' },
    { title: 'Leadership Development', path: '/services/leadership-development', desc: 'Front-line supervisor academies, executive coaching, and EQ training.' }
  ];

  return (
    <div className="w-full bg-white">
      <PageHeader 
        title="Our Services & Methodology" 
        description="We operate as one connected system. You can start with a single service or build a comprehensive program." 
      />

      {/* Workflow Process Section */}
      <section className="py-24 bg-brand-light border-b border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20 max-w-3xl mx-auto">
            <h2 className="text-brand-green font-bold tracking-wide uppercase text-sm mb-2">Our Process</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-brand-navy mb-6 tracking-tight">The Workflow</h3>
            <p className="text-lg text-slate-600 font-medium leading-relaxed">
              True preparedness is a cycle, not a destination. We partner with organizations to systematically build lasting resilience.
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Center Connecting Line */}
            <div className="absolute left-8 sm:left-10 md:left-1/2 top-4 bottom-4 w-1 bg-gradient-to-b from-brand-light via-slate-200 to-brand-light md:-translate-x-1/2"></div>

            <div className="space-y-12 sm:space-y-16">
              {steps.map((step, i) => {
                const isEven = i % 2 === 0;
                
                return (
                  <motion.div 
                    key={i} 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="relative flex items-center group"
                  >
                    {/* Node Icon */}
                    <div className="absolute left-8 sm:left-10 md:left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 sm:w-16 sm:h-16 bg-white border-4 border-slate-200 group-hover:border-brand-green text-slate-400 group-hover:text-brand-navy rounded-full flex items-center justify-center z-10 transition-all duration-300 shadow-sm group-hover:shadow-md">
                      <SafeIcon icon={step.icon} className="text-xl sm:text-2xl" />
                    </div>

                    {/* Content Card */}
                    <div className={`w-full flex ${isEven ? 'md:justify-start' : 'md:justify-end'}`}>
                      <div className={`w-full md:w-1/2 ${isEven ? 'md:pr-12 lg:pr-16 text-left md:text-right' : 'md:pl-12 lg:pl-16 text-left'} pl-20 sm:pl-24 md:pl-0`}>
                        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200 group-hover:shadow-xl group-hover:border-brand-green transition-all relative overflow-hidden">
                          {/* Background Number */}
                          <div className={`text-7xl sm:text-8xl font-black text-slate-50 absolute -top-4 sm:-top-6 ${isEven ? 'md:-left-4 md:right-auto -right-4' : '-right-4'} select-none z-0 group-hover:text-brand-green/5 transition-colors`}>
                            {step.num}
                          </div>
                          
                          {/* Text Content */}
                          <div className="relative z-10">
                            <h4 className="text-xl sm:text-2xl font-bold text-brand-navy mb-2 sm:mb-3 group-hover:text-brand-green transition-colors">
                              {step.title}
                            </h4>
                            <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                              {step.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-12 text-center tracking-tight">Explore Our Solutions</h2>
        
        <div className="space-y-4">
          {services.map((service, i) => (
            <Link 
              key={i} 
              to={service.path} 
              className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-white border border-slate-200 rounded-xl hover:shadow-md hover:border-brand-green transition-all group"
            >
              <div>
                <h3 className="text-xl font-bold text-brand-navy mb-2 group-hover:text-brand-green transition-colors">{service.title}</h3>
                <p className="text-slate-600 font-medium">{service.desc}</p>
              </div>
              <div className="mt-6 sm:mt-0 shrink-0">
                <div className="w-12 h-12 rounded-full bg-brand-light flex items-center justify-center text-brand-navy group-hover:bg-brand-green group-hover:text-white transition-colors shadow-sm">
                  <SafeIcon icon={FiIcons.FiArrowRight} className="text-lg" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ServicesOverview;